// src/services/agentApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const agentApi = createApi({
  reducerPath: 'agentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business-agent-config`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['AgentConfig', 'AgentMetrics', 'AgentHistory'],
  endpoints: (builder) => ({
    // Get all agent configurations for a business
    getBusinessAgents: builder.query({
      query: (businessId) => `/${businessId}`,
      providesTags: ['AgentConfig'],
    }),
    
    // Get configuration for a specific agent
    getAgentConfiguration: builder.query({
      query: ({ businessId, agentType }) => `/${businessId}/agent/${agentType}`,
      providesTags: (_, __, { agentType }) => 
        [{ type: 'AgentConfig', id: agentType }],
    }),
    
    // Update agent configuration
    updateAgentConfiguration: builder.mutation({
      query: ({ businessId, agentType, configData }) => ({
        url: `/${businessId}/agent/${agentType}/configuration`,
        method: 'PUT',
        body: configData,
      }),
      invalidatesTags: (_, __, { agentType }) => 
        [{ type: 'AgentConfig', id: agentType }, 'AgentConfig'],
    }),
    
    // Enable an agent
    enableAgent: builder.mutation({
      query: ({ businessId, agentType }) => ({
        url: `/${businessId}/agent/${agentType}/enable`,
        method: 'PUT',
      }),
      invalidatesTags: (_, __, { agentType }) => 
        [{ type: 'AgentConfig', id: agentType }, 'AgentConfig'],
    }),
    
    // Disable an agent
    disableAgent: builder.mutation({
      query: ({ businessId, agentType }) => ({
        url: `/${businessId}/agent/${agentType}/disable`,
        method: 'PUT',
      }),
      invalidatesTags: (_, __, { agentType }) => 
        [{ type: 'AgentConfig', id: agentType }, 'AgentConfig'],
    }),

    // Get agent metrics
    getAgentMetrics: builder.query({
      query: ({ businessId, agentType, period = '30d' }) => ({
        url: `business-agent-config/${businessId}/agent/${agentType}/metrics`,
        method: 'GET',
        headers: {
          'business-x-api-key': localStorage.getItem('businessApiKey') || ''
        },
        params: { period }
      }),
      providesTags: ['AgentMetrics']
    }),

    // Get agent history
    getAgentHistory: builder.query({
      query: ({ businessId, agentType, page = 1, limit = 10, period = '30d' }) => ({
        url: `/${businessId}/agent/${agentType}/history`,
        method: 'GET',
        headers: {
          'business-x-api-key': localStorage.getItem('businessApiKey') || ''
        },
        params: { page, limit, period }
      }),
      providesTags: ['AgentHistory']
    })
  }),
})

export const {
  useGetBusinessAgentsQuery,
  useGetAgentConfigurationQuery,
  useUpdateAgentConfigurationMutation,
  useEnableAgentMutation,
  useDisableAgentMutation,
  useGetAgentMetricsQuery,
  useGetAgentHistoryQuery,
} = agentApi